import { useRef, useState } from "react";
import { motion } from "framer-motion";

/* Browser window mockup wrapping a screenshot */
export const BrowserMock = ({ src, alt, domain = "", className = "" }) => (
  <div className={`mock-browser flex flex-col ${className}`}>
    <div className="mock-bar shrink-0">
      <span className="mock-dot" style={{ background: "#ff5f57" }} />
      <span className="mock-dot" style={{ background: "#febc2e" }} />
      <span className="mock-dot" style={{ background: "#28c840" }} />
      {domain ? (
        <div className="ml-3 flex-1 bg-white/5 border border-white/10 rounded-full px-3 py-0.5 flex items-center gap-1.5 max-w-[200px]">
          <span className="text-emerald-400" style={{ fontSize: "8px" }}>&#9679;</span>
          <span className="text-[10px] text-neutral-400 truncate">{domain}</span>
        </div>
      ) : null}
    </div>
    <div className="flex-1 w-full relative overflow-hidden bg-white">
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="w-full h-full object-cover"
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = "/brand/launchpad-preview.png";
        }}
      />
    </div>
  </div>
);

/* Phone mockup */
export const PhoneMock = ({ src, alt, className = "" }) => (
  <div className={`mock-phone ${className}`}>
    <img src={src} alt={alt} loading="lazy" className="w-full h-full object-cover" />
  </div>
);

/* Floating glass stat chip */
export const FloatingStat = ({ value, label, className = "", delay = 0 }) => (
  <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay, duration: 0.6 }}
    className={`rounded-2xl px-5 py-3.5 border border-white/10 ${className}`}
    style={{ background: "rgba(11,11,11,0.9)", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)", boxShadow: "0 20px 40px -18px rgba(0,0,0,0.5)" }}>
    <div className="font-display text-2xl font-bold text-white leading-none">{value}</div>
    <div className="text-[11px] text-neutral-400 mt-1 uppercase tracking-wider">{label}</div>
  </motion.div>
);

/* 3D Intelligent Digital System Element with dynamic motion, interactive perspective tilt, and cards moving around */
export const AbstractOrb = () => {
  const containerRef = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseMove = (e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    setTilt({ x: -(y * 14), y: x * 14 });
  };

  const handleMouseLeave = () => {
    setTilt({ x: 0, y: 0 });
    setIsHovered(false);
  };

  return (
    <div
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleMouseLeave}
      className="relative w-full max-w-[460px] lg:max-w-[520px] mx-auto py-3 sm:py-5 select-none"
      style={{ perspective: "1200px" }}
    >
      {/* Dynamic Ambient Volumetric Orange Backlight Glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[85%] h-[85%] rounded-full pointer-events-none transition-all duration-700"
        style={{
          background: isHovered
            ? "radial-gradient(circle, rgba(255,90,31,0.28) 0%, rgba(255,140,40,0.15) 45%, transparent 75%)"
            : "radial-gradient(circle, rgba(255,90,31,0.18) 0%, rgba(255,140,40,0.08) 45%, transparent 75%)",
          filter: "blur(48px)",
        }}
      />

      {/* Floating Card: LEFT SIDE of the 3D Element */}
      <motion.div
        animate={{
          x: [-6, 6, -6],
          y: [-8, 8, -8],
          rotate: [-1.5, 1.5, -1.5],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        style={{
          transform: `perspective(1200px) rotateX(${tilt.x * 0.7}deg) rotateY(${tilt.y * 0.7}deg)`,
          transition: isHovered ? "transform 0.15s ease-out" : "transform 0.8s cubic-bezier(0.16, 1, 0.3, 1)",
        }}
        className="glass-card rounded-2xl px-3.5 py-2.5 sm:px-4 sm:py-3 absolute top-1 sm:top-2 left-0 sm:left-2 lg:-left-2 z-40 bg-white/95 backdrop-blur-xl border border-black/10 shadow-[0_16px_36px_-10px_rgba(0,0,0,0.14)] w-max whitespace-nowrap pointer-events-auto hover:scale-105 transition-transform"
      >
        <div className="flex items-center gap-2 mb-0.5">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
          </span>
          <span className="text-[10px] font-bold text-[#FF5A1F] uppercase tracking-wider">Platform Uptime</span>
        </div>
        <div className="font-display font-bold text-[#0B0B0B] text-base sm:text-lg pl-4">99.99%</div>
      </motion.div>

      {/* Floating Card: RIGHT SIDE of the 3D Element */}
      <motion.div
        animate={{
          x: [6, -6, 6],
          y: [8, -8, 8],
          rotate: [1.5, -1.5, 1.5],
        }}
        transition={{
          duration: 6.5,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 0.5,
        }}
        style={{
          transform: `perspective(1200px) rotateX(${tilt.x * 0.7}deg) rotateY(${tilt.y * 0.7}deg)`,
          transition: isHovered ? "transform 0.15s ease-out" : "transform 0.8s cubic-bezier(0.16, 1, 0.3, 1)",
        }}
        className="glass-card rounded-2xl px-3.5 py-2.5 sm:px-4 sm:py-3 absolute top-3 sm:top-6 right-0 sm:right-2 lg:-right-2 z-40 bg-white/95 backdrop-blur-xl border border-black/10 shadow-[0_16px_36px_-10px_rgba(0,0,0,0.14)] w-max whitespace-nowrap pointer-events-auto hover:scale-105 transition-transform"
      >
        <div className="flex items-center gap-2 mb-0.5">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF5A1F] opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FF5A1F]" />
          </span>
          <span className="text-[10px] font-bold text-[#FF5A1F] uppercase tracking-wider">Infrastructure</span>
        </div>
        <div className="font-display font-bold text-[#0B0B0B] text-base sm:text-lg pl-4">24/7 Monitoring</div>
      </motion.div>

      {/* Floating 3D Standalone Motion Element (Free from any card frame) */}
      <motion.div
        animate={{
          y: [-10, 10, -10],
          rotateZ: [-0.8, 0.8, -0.8],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        style={{
          transform: `perspective(1200px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
          transition: isHovered ? "transform 0.15s ease-out" : "transform 0.8s cubic-bezier(0.16, 1, 0.3, 1)",
          transformStyle: "preserve-3d",
        }}
        className="relative z-10 w-full flex items-center justify-center px-4 sm:px-8"
      >
        <img
          src="/brand/3d-tech-emblem-isolated.png?v=7"
          alt="Neuravolt 3D Intelligent System Architecture"
          className="w-full max-w-[240px] sm:max-w-[280px] lg:max-w-[310px] h-auto object-contain block mx-auto select-none pointer-events-none drop-shadow-[0_22px_40px_rgba(255,90,31,0.22)]"
          loading="eager"
        />

        {/* Orbiting Satellite Particle 1 (Orange Spark) */}
        <motion.div
          animate={{
            x: [0, 14, 0, -14, 0],
            y: [-12, 0, 12, 0, -12],
            scale: [1, 1.25, 1, 0.9, 1],
            opacity: [0.8, 1, 0.8, 0.9, 0.8],
          }}
          transition={{ duration: 4.8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-8 right-6 sm:right-10 w-3.5 h-3.5 rounded-full bg-gradient-to-br from-[#FF9E59] to-[#FF5A1F] shadow-[0_0_16px_rgba(255,90,31,0.95)] pointer-events-none z-20"
        />

        {/* Orbiting Satellite Particle 2 (Chrome Silver) */}
        <motion.div
          animate={{
            x: [0, -12, 0, 12, 0],
            y: [10, 0, -10, 0, 10],
            scale: [1, 1.2, 1, 0.95, 1],
            opacity: [0.7, 1, 0.7, 0.9, 0.7],
          }}
          transition={{ duration: 5.8, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
          className="absolute bottom-12 left-8 sm:left-12 w-2.5 h-2.5 rounded-full bg-gradient-to-br from-white to-neutral-400 shadow-[0_0_12px_rgba(255,255,255,0.9)] pointer-events-none z-20"
        />
      </motion.div>

      {/* Synchronized Physical Ground Shadow */}
      <motion.div
        animate={{
          scale: [1, 0.86, 1],
          opacity: [0.32, 0.18, 0.32],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="w-3/5 h-6 mx-auto -mt-2 rounded-full bg-gradient-to-r from-transparent via-[#FF5A1F]/25 to-transparent blur-md pointer-events-none"
      />
    </div>
  );
};



/* Section wrapper that tracks mouse for spotlight glow */
export const Spotlight = ({ children, className = "", ...props }) => {
  const ref = useRef(null);
  const handlePos = (e) => {
    const el = ref.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    const y = e.touches ? e.touches[0].clientY : e.clientY;
    el.style.setProperty("--mx", `${x - r.left}px`);
    el.style.setProperty("--my", `${y - r.top}px`);
  };
  return <div ref={ref} onMouseMove={handlePos} onTouchStart={handlePos} onTouchMove={handlePos} className={`spotlight ${className}`} {...props}>{children}</div>;
};
